"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

/* ------------------------------------------------------------------ */
/*  Module-level regexes                                              */
/* ------------------------------------------------------------------ */

/*
 * Case-insensitive backreference: "the the" / "The the" → one word.
 *
 * The word class was `[A-Za-z'’-]` and the boundaries were `\b`, and BOTH had to change
 * — either one left alone keeps the mode dead outside ASCII. The class is what made
 * "это это тест тест", "هذا هذا اختبار", "यह यह एक एक", "이것은 이것은", "đây đây" and
 * "für für" pass through with nothing removed; `\b` is defined on `[A-Za-z0-9_]` even
 * under the `u` flag, so it does not exist at the edge of a Cyrillic or Arabic word and
 * the pattern could not anchor there either. `\b` has no Unicode form, so the anchors
 * are written as explicit lookarounds instead.
 *
 * `\p{M}` is in the captured class because Devanagari writes its vowels as combining
 * marks: without it the capture stops at the first matra, so "यह यह" would match on a
 * fragment and collapse the wrong span. It is in the lookaround class for the same
 * reason. Accented Latin was worse than untouched — "für für" already matched on the
 * bare "f", found no second "f" and silently gave up.
 *
 * The lookarounds carry `\p{N}` and `_` while the capture does not, mirroring `\b`'s
 * word-character set exactly: that is what keeps "the the2" and "2the the" un-collapsed
 * in English, as they were before.
 */
const RE_CONSECUTIVE_DUP =
  /(?<![\p{L}\p{N}\p{M}_])([\p{L}\p{M}'’-]+)((?:[^\S\n]+\1)+)(?![\p{L}\p{N}\p{M}_])/giu;
const RE_INLINE_SPACES = /[^\S\n]+/;
const RE_SPLIT_ALL = /\s+/;

type Mode = "consecutive" | "all";

const MODES: { value: Mode }[] = [{ value: "consecutive" }, { value: "all" }];

/* ------------------------------------------------------------------ */
/*  Removal logic                                                     */
/* ------------------------------------------------------------------ */

interface RemovalResult {
  output: string;
  removed: number;
}

export function removeConsecutive(text: string): RemovalResult {
  let removed = 0;
  const output = text.replace(
    RE_CONSECUTIVE_DUP,
    (_full, first: string, rest: string) => {
      removed += rest.split(RE_INLINE_SPACES).filter(Boolean).length;
      return first;
    }
  );
  return { output, removed };
}

export function removeAllDuplicates(text: string): RemovalResult {
  const seen = new Set<string>();
  let removed = 0;
  const outputLines: string[] = [];
  for (const line of text.split("\n")) {
    const words = line.split(RE_INLINE_SPACES).filter(Boolean);
    const kept = words.filter((word) => {
      const key = word.toLowerCase();
      if (seen.has(key)) {
        removed += 1;
        return false;
      }
      seen.add(key);
      return true;
    });
    // A line whose every word was a repeat goes away with them; a line that was blank
    // to begin with is the reader's own paragraph break and stays.
    if (kept.length > 0 || words.length === 0) {
      outputLines.push(kept.join(" "));
    }
  }
  return { output: outputLines.join("\n"), removed };
}

function removeDuplicates(text: string, mode: Mode): RemovalResult {
  if (mode === "consecutive") {
    return removeConsecutive(text);
  }
  return removeAllDuplicates(text);
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const DUPLICATE_WORD_REMOVER_STRINGS = {
  mode: "Mode",
  inputText: "Input text",
  word: "one: word|other: words",
  removeDuplicates: "Remove Duplicates",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  result: "Result",
  duplicateWordsRemoved: "Duplicate Words Removed",
  wordsRemaining: "Words Remaining",
  modeLabels: {
    consecutive: "Consecutive repeats only",
    all: "All duplicates in a word list",
  },
  modeHints: {
    consecutive: 'Collapse "the the" → "the" — safe for prose.',
    all: "Keep the first occurrence of each word — for lists.",
  },
  placeholders: {
    consecutive:
      "Paste prose with accidental repeats, e.g. I went to to the the store.",
    all: "Paste a word list — one word per line or separated by spaces.",
  },
} as const;

export default function DuplicateWordRemoverWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("consecutive");
  const [result, setResult] = useState<RemovalResult | null>(null);
  const [copied, setCopied] = useState(false);

  const inputWordCount = input
    .trim()
    .split(RE_SPLIT_ALL)
    .filter(Boolean).length;

  function handleRemove() {
    if (input.trim().length === 0) {
      return;
    }
    setResult(removeDuplicates(input, mode));
    setCopied(false);
  }

  function handleCopy() {
    if (!result) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setResult(null);
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(DUPLICATE_WORD_REMOVER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          {MODES.map((m) => (
            <label
              className="flex cursor-pointer flex-col gap-1 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <span className="flex items-center gap-2">
                <input
                  checked={mode === m.value}
                  className="accent-primary"
                  name="dup-mode"
                  onChange={() => setMode(m.value)}
                  type="radio"
                  value={m.value}
                />
                {strings.modeLabels[m.value]}
              </span>
              <span className="text-muted-foreground text-xs">
                {strings.modeHints[m.value]}
              </span>
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="dup-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="dup-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholders[mode]}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(inputWordCount)}
          {tPlural(strings.word, locale, inputWordCount)}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleRemove}
          type="button"
        >
          {strings.removeDuplicates}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!result}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && !result}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {result && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="dup-output"
          >
            {strings.result}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="dup-output"
            readOnly
            value={result.output}
          />
        </div>
      )}
      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            label={strings.duplicateWordsRemoved}
            value={fmt(result.removed)}
          />
          <StatCard
            label={strings.wordsRemaining}
            value={
              result.output.trim().length === 0
                ? 0
                : fmt(
                    result.output.trim().split(RE_SPLIT_ALL).filter(Boolean)
                      .length
                  )
            }
          />
        </div>
      )}
    </>
  );
}
